/**
 * @file BinsTable.tsx
 * @description Таблица ячеек на `≥lg` (phase19.22 шаг 5): галочки под печать
 *   этикеток, адрес, место, содержимое, статус и действия. На `<lg` тот же
 *   список — карточками (`BinCard`).
 *
 * Галочка в шапке отмечает все видимые активные ячейки (с учётом поиска) —
 * на десктопе это быстрее, чем по одной. Подписи и меню — `bin-view.ts`.
 */

import { PackagePlus } from "lucide-react";

import { binRowActions, binStatus, binSummaryText } from "./bin-view";
import type { BinSelection } from "./useBinSelection";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { Checkbox } from "../../ui/checkbox";
import { RowActionsMenu } from "../../ui/RowActionsMenu";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../ui/table";
import { formatBinHuman, type StorageBinDto } from "../../../lib/api-bins";

interface Props {
  bins: StorageBinDto[];
  /** Нет — колонки галочек нет (нет права печати). */
  selection: BinSelection | null;
  onOpen: (bin: StorageBinDto) => void;
  onPut: (bin: StorageBinDto) => void;
  onArchive: (bin: StorageBinDto) => void;
  onRestore: (bin: StorageBinDto) => void;
}

export function BinsTable({ bins, selection, onOpen, onPut, onArchive, onRestore }: Props) {
  const activeIds = bins.filter((b) => b.isActive).map((b) => b.id);
  const allChecked = !!selection && activeIds.length > 0 && activeIds.every(selection.isSelected);

  return (
    <Table>
      <TableHeader>
        <TableRow>
          {selection ? (
            <TableHead className="w-12">
              <label className="inline-flex size-11 cursor-pointer items-center justify-center">
                <Checkbox
                  checked={allChecked}
                  disabled={activeIds.length === 0}
                  onCheckedChange={() => selection.setMany(activeIds, !allChecked)}
                  aria-label="Выбрать все показанные ячейки"
                  className="size-5"
                />
              </label>
            </TableHead>
          ) : null}
          <TableHead>Адрес</TableHead>
          <TableHead>Место</TableHead>
          <TableHead>Содержимое</TableHead>
          <TableHead>Статус</TableHead>
          <TableHead className="text-right">Действия</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {bins.map((bin) => {
          const status = binStatus(bin);
          return (
            <TableRow key={bin.id} data-state={selection?.isSelected(bin.id) ? "selected" : undefined}>
              {selection ? (
                <TableCell>
                  {bin.isActive ? (
                    // Зона нажатия 44px вокруг квадрата 20px (как в «Остатках»).
                    <label className="inline-flex size-11 cursor-pointer items-center justify-center">
                      <Checkbox
                        checked={selection.isSelected(bin.id)}
                        onCheckedChange={() => selection.toggle(bin.id)}
                        aria-label={`Выбрать ячейку ${bin.code} для печати этикеток`}
                        className="size-5"
                      />
                    </label>
                  ) : null}
                </TableCell>
              ) : null}
              <TableCell>
                <button
                  type="button"
                  onClick={() => onOpen(bin)}
                  className="font-mono text-base font-medium text-primary hover:underline"
                >
                  {bin.code}
                </button>
              </TableCell>
              <TableCell className="text-on-surface-variant">{formatBinHuman(bin)}</TableCell>
              <TableCell className="tabular-nums">{binSummaryText(bin)}</TableCell>
              <TableCell>
                <Badge variant={status.variant}>{status.label}</Badge>
              </TableCell>
              <TableCell>
                <div className="flex items-center justify-end gap-2">
                  {bin.isActive ? (
                    <Button variant="outline" className="min-h-12" onClick={() => onPut(bin)}>
                      <PackagePlus className="size-4" />
                      <span className="ml-1.5">Положить товар</span>
                    </Button>
                  ) : null}
                  <RowActionsMenu
                    size="wms"
                    actions={binRowActions(bin, {
                      onOpen: () => onOpen(bin),
                      onArchive: () => onArchive(bin),
                      onRestore: () => onRestore(bin),
                    })}
                    title={`Действия с ячейкой ${bin.code}`}
                    ariaLabel={`Другие действия с ячейкой ${bin.code}`}
                  />
                </div>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
